// File: src/pages/AccountCategoryPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Spinner,
} from "reactstrap";
import { FaPlus } from "react-icons/fa";
import { Trash2 } from "react-feather";
import swal from "sweetalert";

// ✅ Use your canonical paths under src/import (same as ProductListCompact)
import {
  _PostSave,
  _GetByUrl,
  _Delete,
} from "../import/ApiEndPoint/ApiCalling"; // _Delete not used but left for parity
import { accountSave, viewAccount } from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";

const injectOnce = () => {
  if (document.getElementById("acc-inline-css")) return;
  const s = document.createElement("style");
  s.id = "acc-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .p-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .p-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:900px; }
    .p-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap; }
    .p-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:13px; text-align:center; }
    .p-table tbody tr:hover{ background:#f9fbff; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px; }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-del{ background:#ec1809; }
  `;
  document.head.appendChild(s);
};

const groupOptions = [
  "Purchase Accounts",
  "Sales Accounts",
  "Duties and Taxes",
  "Direct Expenses",
  "Indirect Expenses",
  "Direct Income",
  "Indirect Income",
  "Bank Accounts",
  "Deposit Accounts",
  "Capital Accounts",
  "Fixed Assets",
  "Current Assets",
  "Loans Liabilities",
  "Current Liabilities",
  "Sundry Creditor",
  "Loans and Advances (Assets)",
  "Suspense A/c",
  "Misc. Expense (ASSET)",
  "Stock-in-hand",
  "Investments",
];

const ledgerOptionsByGroup = [
  {
    name: "Purchase Accounts",
    accountType: ["Trading Account"],
    side: "Debit",
    options: [
      "Purchases",
      "Purchase Local 12%",
      "Purchase Interstate 12%",
      "Purchase Local 0%",
      "Purchase Interstate 0%",
      "Purchase (Composition)",
      "Purchase Exempt (Unregistered Dealer)",
      "Purchase Local (Exempt Registered)",
      "Purchase Taxable (Unregistered Dealer)",
      "Purchase Nil Rated (Unregistered Dealer)",
      "Purchase Reverse Charge",
      "Purchase Import Taxable 12%",
      "Purchase Import Exempt",
      "Purchase Import Nil Rated",
      "Purchase (Own Branch)",
      "Purchase Return",
    ],
  },
  {
    name: "Sales Accounts",
    accountType: ["Trading Account"],
    side: "Credit",
    options: [
      "Sales Account",
      "Sales Local 12% (Registered)",
      "Sales Interstate 12%",
      "Sales Local Nil Rated",
      "Sales Interstate Nil Rated",
      "Sales Export With Bond",
      "Sales Export Taxable",
      "Export (0%)",
      "Sales Local (Exempt Registered)",
      "Sale To Consumer (Taxable 12%)",
      "Sale To Consumer (0%)",
      "Sale To Consumer (Exempt)",
      "Sales (Own Branch)",
      "Sale Return",
    ],
  },
  {
    name: "Duties and Taxes",
    accountType: ["Balance Sheet"],
    side: "Liability",
    options: [
      "INPUT CGST",
      "INPUT SGST",
      "INPUT IGST",
      "INPUT CESS",
      "OUTPUT GST",
      "OUTPUT SGST",
      "OUTPUT IGST",
      "OUTPUT CESS",
      "Excise Duty Payable",
      "Service Tax Payable",
      "TDS Payables",
      "Input VAT Accounts",
      "Output VAT Accounts",
      "Cenvat Accounts",
      "Sales Tax",
      "Income Tax",
      "VAT Payable",
    ],
  },
  {
    name: "Direct Expenses",
    accountType: ["Trading Account"],
    side: "Debit",
    options: [
      "Direct Expenses",
      "Labour",
      "Power",
      "Electricity Expense (Factory)",
      "Loading Unloading Expense",
      "Warehousing Expenses",
      "Custom Clearing Charges",
      "Carriage",
      "Freight & Cartage",
      "Import Duty",
      "Wages",
      "Coal & Fuel",
      "Coal, Gas & Water of Factory",
      "Consumed Material",
      "Export Duty",
      "Wages on Production",
      "Delivery Charges (In Purchase Bill)",
    ],
  },
  {
    name: "Indirect Expenses",
    accountType: ["Profit & Loss Account"],
    side: "Debit",
    options: [
      "Indirect Expenses",
      "Rounded Off",
      "Salary",
      "Advertisement Expense",
      "Maintenance Expense",
      "Rent Expense",
      "Director Remuneration Expense",
      "Bad Debts",
      "Printing Expense",
      "Stationary Expense",
      "Foreign Exchange Fluctuation",
      "Audit Fees",
      "Professional Charges",
      "Legal Expenses/Charges",
      "Depreciation Expenses",
      "Interest Expense",
      "Penalty",
      "Royalty",
      "Bank Charges",
      "Commission Allowed",
      "Discount Allowed",
      "Donation & Charity",
      "Free Sample",
      "Insurance Premium",
      "Interest on Loan",
      "Legal Charge",
      "Loss by Fire",
      "Postage & Courier",
      "Repair Charge",
      "Taxi Fare",
      "Telephone Charge",
      "Travelling Expenses",
      "Outstanding Expenses",
      "Accrued Expenses",
      "Bad Debt",
      "Loss on Theft",
      "Depreciation",
      "Coffee Expenses",
      "Coke Expenses",
      "Manager’s Commission",
      "UPTT",
      "Fuel Expenses A/c",
      "Liability of Expenses",
      "Preliminary Expenses A/c",
      "Professional Fees",
    ],
  },
  {
    name: "Direct Income",
    accountType: ["Profit & Loss Account"],
    side: "Credit",
    options: ["Direct Income"],
  },
  {
    name: "Indirect Income",
    accountType: ["Profit & Loss Account"],
    side: "Credit",
    options: ["Indirect Income", "Discount Received", "Interest on Investment"],
  },
  {
    name: "Bank Accounts",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: [
      "All Bank FD Account",
      "(Personal Savings Account and FD not recorded)",
    ],
  },
  {
    name: "Deposit Accounts",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: ["Security Deposit", "Electricity Deposit", "Rent Deposit"],
  },
  {
    name: "Capital Accounts",
    accountType: ["Balance Sheet"],
    side: "Liability",
    options: [
      "Capital Accounts",
      "Share Capital",
      "Partner Capital Account",
      "Partner Current Account",
      "Proprietor Account",
      "Drawings",
      "Life Insurance",
      "Equity Capital A/c",
      "Partners Capital A/c",
    ],
  },
  {
    name: "Fixed Assets",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: [
      "Furniture",
      "Machine",
      "Plant and Machinery",
      "Mobile",
      "Computers",
      "Furniture and Fittings",
      "Car",
      "Scooter",
      "Laptops",
      "Office Lighting",
      "Land & Building",
      "Goodwill",
      "Factory Lighting",
      "Air Conditioner",
      "Accumulated Depreciation",
    ],
  },
  {
    name: "Current Assets",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: [
      "Prepaid Maintenance Expense",
      "Prepaid Expense",
      "Prepaid Rent",
      "Prepaid Insurance Charges",
      "Interest Receivables",
      "Bill Receivable",
      "Accrued Income",
      "Mutual Fund",
      "CGST Credit",
      "SGST Credit",
      "IGST Credit",
    ],
  },
  {
    name: "Loans Liabilities",
    accountType: ["Balance Sheet"],
    side: "Liability",
    options: [
      "Debenture A/c",
      "Loans From Bank",
      "Loans From Outside Party",
      "Loans From Aravind (Friend)",
    ],
  },
  {
    name: "Current Liabilities",
    accountType: ["Balance Sheet"],
    side: "Liability",
    options: [
      "Bill Drawn",
      "Bill Payable",
      "CGST Payable",
      "SGST Payable",
      "IGST Payable",
    ],
  },
  {
    name: "Sundry Creditor",
    accountType: ["Balance Sheet"],
    side: "Liability",
    options: [
      "Any Party from Whom Goods Purchased",
      "Party from Whom any Bill of Expense Received",
    ],
  },
  {
    name: "Loans and Advances (Assets)",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: [
      "Loan Given to Friends",
      "Loan Given to Relatives",
      "Loan Given to Related Companies",
      "Advance to Supplier",
    ],
  },
  {
    name: "Suspense A/c",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: [
      "Any payment or receipt from party whose name not known",
      "Suspense",
    ],
  },
  {
    name: "Misc. Expense (ASSET)",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: ["Preliminary Expenses"],
  },
  {
    name: "Stock-in-hand",
    accountType: ["Balance Sheet", "Trading Account"],
    side: "Asset",
    options: ["Stock", "Closing Stock", "Consignment Stock", "Opening Stock"],
  },
  {
    name: "Investments",
    accountType: ["Balance Sheet"],
    side: "Asset",
    options: [],
  },
];

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function AccountCategoryPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]); // API: res?.viewAccounts?.Accounts
  const [search, setSearch] = useState("");
  const [permissions, setPermissions] = useState({});
  const [modal, setModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    groupName: "",
    customGroupName: "",
    side: "",
    accountType: [],
    ledgerName: "",
    manualLedger: "",
  });

  useEffect(() => injectOnce(), []);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      setPermissions(CheckPermission("Accounts") || {});
      if (!user?.database) {
        setRows([]);
      } else {
        const res = await _GetByUrl(viewAccount, user.database);
        setRows(
          Array.isArray(res?.viewAccounts?.Accounts)
            ? res.viewAccounts.Accounts
            : []
        );
      }
    } catch (err) {
      console.error("[Accounts] load error:", err);
      setRows([]);
      swal("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); /* eslint-disable-next-line */
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const hit = (v) => toStr(v).toLowerCase().includes(term);
    return rows.filter(
      (r) =>
        hit(r?.groupName) ||
        hit(r?.ledgerName) ||
        hit(
          Array.isArray(r?.accountType)
            ? r.accountType.join(", ")
            : r?.accountType
        ) ||
        hit(r?.side)
    );
  }, [rows, search]);

  const selectedGroupMeta = useMemo(
    () => ledgerOptionsByGroup.find((g) => g.name === newCategory.groupName),
    [newCategory.groupName]
  );

  const toggleModal = () => {
    setModal((m) => !m);
    setNewCategory({
      groupName: "",
      customGroupName: "",
      side: "",
      accountType: [],
      ledgerName: "",
      manualLedger: "",
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    const usingCustomGroup =
      !newCategory.groupName && newCategory.customGroupName;
    if (!newCategory.groupName && !newCategory.customGroupName)
      return swal("Please select or enter a Group Name");
    if (!newCategory.ledgerName && !newCategory.manualLedger)
      return swal("Please select or enter a Ledger Name");

    const finalLedgerName = newCategory.ledgerName || newCategory.manualLedger;
    const finalGroupName = newCategory.groupName || newCategory.customGroupName;
    const finalAccountType = newCategory.accountType?.length
      ? newCategory.accountType
      : selectedGroupMeta?.accountType || [];
    const finalSide = selectedGroupMeta?.side || newCategory.side || "";

    if (usingCustomGroup && !finalSide)
      return swal("Please select Side for custom group");
    if (usingCustomGroup && finalAccountType.length === 0)
      return swal("Please select Account Type for custom group");

    const payload = {
      created_by: user?._id,
      database: user?.database,
      Accounts: [
        {
          groupName: finalGroupName,
          ledgerName: finalLedgerName,
          side: finalSide,
          accountType: finalAccountType,
        },
      ],
    };

    try {
      const res = await _PostSave(accountSave, payload);
      if (res?.status) {
        swal("Account added successfully");
        await fetchData();
        toggleModal();
      } else {
        swal("Error in saving account");
        await fetchData();
      }
    } catch (err) {
      console.error(err);
      swal("Error in saving account");
    }
  };

  const onDelete = async (id) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then(async (value) => {
      if (value !== "delete") return;
      try {
        const res = await _GetByUrl(viewAccount, user?.database);
        const mainId = res?.viewAccounts?._id;
        if (!mainId) return swal("Unable to resolve account group id");
        // deleteAccounts(mainId, id) is defined in your codebase;
        // keeping the same signature you used earlier:
        const delRes = await (
          await import("../import/ApiEndPoint/ApiCalling")
        ).deleteAccounts(mainId, id);
        if (delRes?.status) {
          swal("Deleted", "Account Removed", "success");
          fetchData();
        } else {
          swal("Error", "Could not delete account", "error");
        }
      } catch (err) {
        console.error(err);
        swal("Error", "Could not delete account", "error");
      }
    });
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Toolbar ===== */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Accounts <span className="count-pill">{filtered.length}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search accounts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {permissions?.Create !== false && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                  }}
                  onClick={toggleModal}
                >
                  <FaPlus size={12} /> Add Category
                </Button>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card className="p-card" style={{ padding: 8 }}>
            <div className="p-table-wrap">
              <table className="p-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>GROUP NAME</th>
                    <th>LEDGER NAME</th>
                    <th>SIDE</th>
                    <th>ACCOUNT TYPE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 18, color: "#6b7280" }}>
                        No accounts found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((r, i) => (
                      <tr key={r?._id || `${r.groupName}-${r.ledgerName}-${i}`}>
                        <td>{i + 1}</td>
                        <td style={{ textAlign: "left" }}>
                          {r?.groupName || "-"}
                        </td>
                        <td style={{ textAlign: "left" }}>
                          {r?.ledgerName || "-"}
                        </td>
                        <td>{r?.side || "-"}</td>
                        <td>
                          {Array.isArray(r?.accountType)
                            ? r.accountType.join(", ")
                            : r?.accountType || "-"}
                        </td>
                        <td>
                          <div className="actions">
                            {permissions?.Delete !== false && (
                              <span
                                className="chip chip-del"
                                title="Delete"
                                onClick={() => onDelete(r?._id)}
                              >
                                <Trash2 size={14} color="white" />
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Add Modal ===== */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Add New Category</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleAdd}>
            <FormGroup>
              <Label htmlFor="groupName">Group Name</Label>
              <Input
                type="select"
                id="groupName"
                value={
                  newCategory.groupName === "other"
                    ? "other"
                    : newCategory.groupName
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "other") {
                    setNewCategory({
                      ...newCategory,
                      groupName: "",
                      customGroupName: "",
                      side: "",
                      accountType: [],
                      ledgerName: "",
                    });
                  } else {
                    setNewCategory({
                      ...newCategory,
                      groupName: value,
                      customGroupName: "",
                      side: "",
                      accountType: [],
                    });
                  }
                }}
              >
                <option value="">Select Group</option>
                {groupOptions.map((g, i) => (
                  <option key={i} value={g}>
                    {g}
                  </option>
                ))}
                <option value="other">Other</option>
              </Input>

              {/* Custom group inputs */}
              {!newCategory.groupName && (
                <>
                  <div
                    className="d-flex align-items-center mt-2"
                    style={{ gap: 8 }}
                  >
                    <Input
                      type="text"
                      placeholder="Enter custom group name"
                      value={newCategory.customGroupName || ""}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          customGroupName: e.target.value,
                        })
                      }
                    />
                    <Input
                      type="select"
                      value={newCategory.side || ""}
                      onChange={(e) =>
                        setNewCategory({ ...newCategory, side: e.target.value })
                      }
                    >
                      <option value="">Select Side</option>
                      <option value="Income">Income</option>
                      <option value="Expense">Expense</option>
                      <option value="Asset">Asset</option>
                      <option value="Liability">Liability</option>
                      <option value="Debit">Debit</option>
                      <option value="Credit">Credit</option>
                    </Input>
                  </div>

                  <Label className="mt-2">Select Account Type</Label>
                  <Input
                    type="select"
                    multiple
                    value={newCategory.accountType || []}
                    onChange={(e) => {
                      const selected = Array.from(
                        e.target.selectedOptions,
                        (opt) => opt.value
                      );
                      setNewCategory({
                        ...newCategory,
                        accountType: selected.includes("all")
                          ? [
                              "Trading Account",
                              "Profit & Loss Account",
                              "Balance Sheet",
                            ]
                          : selected,
                      });
                    }}
                  >
                    <option value="all">Select All</option>
                    <option value="Trading Account">Trading</option>
                    <option value="Profit & Loss Account">
                      Profit and Loss
                    </option>
                    <option value="Balance Sheet">Balance Sheet</option>
                  </Input>
                </>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="ledgerName">Ledger Name</Label>
              <Input
                type="select"
                id="ledgerName"
                value={newCategory.ledgerName || ""}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    ledgerName: e.target.value,
                    manualLedger: "",
                  })
                }
              >
                <option value="">Select Ledger</option>
                {selectedGroupMeta?.options?.map((l, i) => (
                  <option key={i} value={l}>
                    {l}
                  </option>
                ))}
              </Input>

              <Label className="mt-2" htmlFor="manualLedger">
                Or enter manually
              </Label>
              <Input
                id="manualLedger"
                type="text"
                placeholder="Enter Ledger Name"
                value={newCategory.manualLedger || ""}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    manualLedger: e.target.value,
                    ledgerName: "",
                  })
                }
              />
            </FormGroup>

            {/* Side & Account Type hints for predefined groups */}
            {newCategory.groupName && (
              <>
                <FormGroup>
                  <Label>Side</Label>
                  <Input
                    type="text"
                    value={selectedGroupMeta?.side || ""}
                    disabled
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Account Type</Label>
                  <Input
                    type="text"
                    value={(selectedGroupMeta?.accountType || []).join(", ")}
                    disabled
                  />
                </FormGroup>
              </>
            )}

            <Button type="submit" color="primary">
              Save Category
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
}
